import './FormSection.css';

import { TabPanel, TabView } from 'primereact/tabview';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import FormItemResolver, {
  FormresolverProps,
} from '../FormItemResolver/FormItemResolver';
import { JSONObject } from '../../../../../types/types';
import { useMaxTabHeight } from '../../../../../hooks/useMaxTabHeight';
import { useSectionTabState } from '../../../../../hooks/useSectionTabState';
import { useRequestDataContext } from '../../../../../hooks/context/useRequestDataContext';
import SeptimLoadingSpinner from '../../../SeptimLoadingSpinner/SeptimLoadingSpinner';
import FormInitProvider from '../../../../../context/FormInitContext';
import {
  FormVisibilityNode,
  getInitialFormItemVisibility,
} from '../../utils/getInitialFormItemVisibility';
import { hasFormItemVisibilityExpression } from '../../utils/hasFormItemVisibilityExpression';
import { resolveFormItemVisibility } from '../../utils/resolveFormItemVisibility';

type SectionItem = FormVisibilityNode & {
  name: string;
  caption?: string;
  items: FormVisibilityNode[];
};
import { debugLogger } from '../../../../../debug/DebugLogger';

const log = debugLogger.scope('visibility');

interface FormSectionProps extends Omit<FormresolverProps, 'formItem'> {
  sectionItems: SectionItem[];
}

const FormSection = ({
  sectionItems,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
}: FormSectionProps) => {
  const hasVisibilityExpressions = useMemo<boolean>(
    () => sectionItems.some(hasFormItemVisibilityExpression),
    [sectionItems]
  );
  const [visibleTabPanels, setVisibleTabPanels] = useState<SectionItem[]>(() =>
    sectionItems.filter(getInitialFormItemVisibility)
  );
  const [resolving, setResolving] = useState<boolean>(hasVisibilityExpressions);
  const groupKey = useMemo(
    () => sectionItems.map((item) => item.name).join('|'),
    [sectionItems]
  );
  const { search } = useLocation();
  // Tab memory is per form token — each opened form keeps its own active tab.
  const tabScopeKey = `form:${new URLSearchParams(search).get('token') ?? 'noToken'}`;
  const [activeIndex, setActiveTab] = useSectionTabState(tabScopeKey, groupKey);
  // Per-tab init flags. renderActiveOnly={false} mounts every panel once, so a
  // single bool would never re-fire on switch — track readiness by tab index.
  const [readyTabs, setReadyTabs] = useState<Record<number, boolean>>({});
  const { tabViewRef, minPanelHeight } = useMaxTabHeight(
    activeIndex,
    !!readyTabs[activeIndex],
    visibleTabPanels.length
  );
  // Use the same request-data shape as the fields inside the section. Form
  // expressions are authored against `form.<field>` and the provider updates
  // this object whenever the user changes a form value.
  const requestData = useRequestDataContext();
  const liveData = (requestData ?? {}) as JSONObject;

  useEffect(() => {
    let isCancelled = false;

    if (!hasVisibilityExpressions) {
      setVisibleTabPanels(sectionItems.filter(getInitialFormItemVisibility));
      setResolving(false);
      return;
    }

    const resolve = async () => {
      const flags = await Promise.all(
        sectionItems.map((item) => resolveFormItemVisibility(item, liveData))
      );

      if (isCancelled) return;

      const visibleSections = sectionItems.filter((_, index) => flags[index]);

      /** @debugFeature visibility Logs resolved form section tabs: which tabs are shown and which ended up hidden (expression or allowVisible). */
      log('form section tabs resolved', () => ({
        shown: visibleSections.map((section) => section.name),
        hidden: sectionItems
          .filter((_, index) => !flags[index])
          .map((section) => section.name),
      }));

      setVisibleTabPanels(visibleSections);
      setResolving(false);
    };

    resolve().catch((error) => {
      if (!isCancelled) {
        console.error('[useResolvedSectionItems]', error);

        /** @debugFeature visibility Logs a failed section tab visibility resolve — one failing expression hides ALL tabs of the section. */
        log('section tabs resolve failed, hiding all tabs', () => ({
          sections: sectionItems.map((section) => section.name),
          error,
        }));

        setVisibleTabPanels([]);
        setResolving(false);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [hasVisibilityExpressions, sectionItems, liveData]);

  // Show skeleton while tab visibility is being resolved on initial load.
  if (resolving) {
    return (
      <div className="form-section-container form-section-skeleton">
        <SeptimLoadingSpinner />
      </div>
    );
  }

  if (visibleTabPanels.length === 0) {
    return null;
  }

  return (
    <TabView
      ref={tabViewRef}
      renderActiveOnly={false}
      className="form-section-container"
      activeIndex={Math.min(
        activeIndex,
        Math.max(0, visibleTabPanels.length - 1)
      )}
      onTabChange={(e) => setActiveTab(e.index)}
    >
      {visibleTabPanels.map((sectionTab, index) => (
        <TabPanel
          key={sectionTab.name}
          header={
            <span
              className="tab-header"
              data-guide-form-section-trigger={sectionTab.name}
            >
              {sectionTab.caption}
            </span>
          }
        >
          {/* Each tab gets its own provider so inputs track initialization independently. */}
          <FormInitProvider
            onInitialized={() =>
              setReadyTabs((prev) =>
                prev[index] ? prev : { ...prev, [index]: true }
              )
            }
          >
            <div
              style={{ minHeight: minPanelHeight || undefined }}
              className="form-section-tab-content-wrapper"
              data-form-section={sectionTab.name}
            >
              {sectionTab.items.map((subItem) => (
                <FormItemResolver
                  key={`${subItem.name}_${subItem.label}`}
                  formItem={subItem}
                  referenceDatasets={referenceDatasets}
                  setReferenceDatasets={setReferenceDatasets}
                  openFullscreenReference={openFullscreenReference}
                />
              ))}
            </div>
          </FormInitProvider>
        </TabPanel>
      ))}
    </TabView>
  );
};

export default FormSection;
